import {assert, unreachable} from "@prosay/common"
import {getLogger} from "@prosay/logging"
import {
    AudioContent,
    Content,
    ImageContent,
    TextContent,
    useSendMessageMutation,
} from "../graphql"
import {blobToDataURL, getMyUserId, getOptimisticId} from "../util"
import {HookOptimisticResponse} from "./types"

const logger = getLogger(__filename)

export const sendMessageMutationResponse = logger.debug(
    (async ({conversationId, imageContent, textContent, voiceContent}) => {
        const id = getOptimisticId(`${conversationId}-${Math.random()}`)
        const now = new Date().toISOString()
        const authorId = (await getMyUserId()) ?? ""

        let type: Content["__typename"] | undefined
        if (textContent) {
            type = "TextContent"
        } else if (voiceContent) {
            type = "AudioContent"
        } else if (imageContent) {
            type = "ImageContent"
        } else {
            unreachable(
                "Expected one of textContent, voiceContent, or imageContent to be truthy.",
            )
        }

        const getContent = async () => {
            assert(type, "type must be TextContent or AudioContent")

            switch (type) {
                case "AudioContent":
                    return {
                        __typename: "AudioContent",
                        audio: {
                            __typename: "StoredAsset",
                            presignedUrl: await blobToDataURL(voiceContent),
                        },
                        id,
                    } as AudioContent
                case "ImageContent":
                    return {
                        __typename: "ImageContent",
                        id,
                        image: {
                            __typename: "StoredAsset",
                            presignedUrl: await blobToDataURL(imageContent),
                        },
                    } as ImageContent
                case "TextContent":
                    return {
                        __typename: "TextContent",
                        content: textContent,
                        id,
                    } as TextContent
                default:
                    unreachable()
            }
        }

        return {
            __typename: "Mutation",
            sendMessage: {
                __typename: "Message",
                author: {
                    __typename: "Client",
                    id: authorId,
                    isMe: true,
                },
                content: await getContent(),
                conversation: {
                    __typename: "Conversation",
                    id: conversationId,
                    lastMessage: {
                        __typename: "Message",
                        id,
                    },
                },
                createdAt: now,
                id,
                updatedAt: now,
            },
        }
    }) as HookOptimisticResponse<typeof useSendMessageMutation>,
    "sendMessageMutationResponse",
)
